import { useContext } from 'react';
import { RegistrationContainer } from '@bumble/app/components/RegistrationContainer';
import { MfaContext } from '@bumble/app/context';
import { useAccount } from '@bumble/app/hooks/use-account';
import { zodResolver } from '@hookform/resolvers/zod';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Divider, InputAdornment, Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { TypeOf, object, string } from 'zod';
import FormInput from '../../components/FormInput';

const mfaLoginSchema = object({
  otp: string(),
});

type MfaLoginInput = TypeOf<typeof mfaLoginSchema>;

export const MultiFactorAuth = () => {
  const mfaContext = useContext(MfaContext);

  const { login, isLoggingIn } = useAccount();

  const methods = useForm<MfaLoginInput>({
    resolver: zodResolver(mfaLoginSchema),
  });

  const { reset, handleSubmit } = methods;

  const submitHandler: SubmitHandler<MfaLoginInput> = (values) => {
    const { otp } = values;
    login({
      username: mfaContext?.user?.email ? mfaContext?.user?.email : '',
      password: mfaContext?.user?.password ? mfaContext?.user?.password : '',
      otp,
      // Carry the selected account through so the backend verifies the OTP for
      // this specific IAM user instead of re-prompting for account selection.
      account_id: mfaContext?.user?.account_id,
    });
    reset();
  };

  return (
    <RegistrationContainer>
      <Typography variant="h3" component="p" py={2}>
        We&apos;ve emailed your passcode
      </Typography>
      <Typography variant="body1" component="p" mb={4}>
        Please enter the code below to continue.
      </Typography>
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(submitHandler)} noValidate autoComplete="off">
          <FormInput
            id="otp"
            name="otp"
            label="Login Code"
            type="text"
            startAdornment={
              <InputAdornment position="start">
                <LockOutlinedIcon />
              </InputAdornment>
            }
          />
          <Box mt={4}>
            <Button variant="contained" size="large" fullWidth type="submit" disabled={isLoggingIn}>
              Login
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <Box py={6}>
        <Divider />
      </Box>
      <Button size="small" variant="text" component={Link} to="/login">
        Try with a different email?
      </Button>
    </RegistrationContainer>
  );
};
